import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, RefreshCw, Check } from 'lucide-react';
import Modal from 'components/ui/Modal';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/use-toast';
import * as TeamsModule from 'modules/teams';

interface Props {
  open: boolean;
  onClose: () => void;
  teamId: string;
}

const JOIN_PATH = (token: string): string =>
  `${window.location.origin}/join/team/${token}`;

// Wayground-style "Invite students to join this class": Method 1 share-link +
// Method 2 short code, both backed by the team's single join token, with a
// "generate new link" control that rotates them.
export const InviteStudentsModal: React.FC<Props> = ({ open, onClose, teamId }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);

  const { joinToken, isLoading } = TeamsModule.Hooks.useJoinToken({
    teamId,
    enabled: open && Boolean(teamId),
  });
  const generate = TeamsModule.Hooks.useGenerateJoinToken();
  const revoke = TeamsModule.Hooks.useRevokeJoinToken();

  // Mint a link on first open if the team has none yet, so the modal always
  // shows something to share (mirrors Wayground). `mintRequestedRef` makes this
  // idempotent per modal-open: without it, React 18 StrictMode's dev
  // double-invoke of this effect (mount → cleanup → mount, before `isPending`
  // has a chance to flip) would fire `generate.mutate` twice and mint two
  // tokens for one open. The ref resets only when the modal closes, so a
  // fresh open (e.g. after a revoke) can mint again.
  const mintRequestedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      mintRequestedRef.current = false;
      return;
    }
    if (isLoading || joinToken || mintRequestedRef.current) return;
    mintRequestedRef.current = true;
    generate.mutate(teamId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isLoading, joinToken, teamId]);

  const link = joinToken ? JOIN_PATH(joinToken.token) : '';

  const copy = async (value: string, which: 'link' | 'code') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
      toast.success(which === 'link' ? t('teams.join_link_copied') : t('teams.join_code_copied'));
    } catch {
      // Clipboard can be blocked (insecure context / permissions) — no-op.
    }
  };

  const handleRegenerate = () => generate.mutate(teamId);

  const handleRevoke = () => {
    if (window.confirm(t('teams.join_revoke_confirm'))) {
      revoke.mutate(teamId, { onSuccess: () => onClose() });
    }
  };

  const expiry = joinToken
    ? new Date(joinToken.expiresAt).toLocaleDateString()
    : '';

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={t('teams.invite_students_title')}
      size="lg"
    >
      <div className="space-y-4">
        {isLoading || (!joinToken && generate.isPending) ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('teams.join_loading')}
          </p>
        ) : !joinToken ? (
          <div className="space-y-3 py-4 text-center">
            <p className="text-sm text-muted-foreground">{t('teams.join_no_link')}</p>
            <Button onClick={handleRegenerate} disabled={generate.isPending}>
              {t('teams.join_generate')}
            </Button>
          </div>
        ) : (
          <>
            {/* Method 1 — share link */}
            <section className="rounded-lg border border-border bg-muted/40 p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-card text-[13px] font-semibold tabular-nums text-foreground/80">
                  1
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold">{t('teams.join_share_link')}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('teams.join_share_link_desc')}
                  </p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                    {/* Truncating on its own full-width row left the share
                        link as "https://app.examing.uz/j…" — unreadable, and
                        this is the value the user is asked to pass on. Wrap on
                        phones; keep one ellipsised line beside the button where
                        the row is shared (sm+). */}
                    <code className="w-full min-w-0 break-all rounded-lg border border-border bg-background px-3 py-2 text-sm sm:flex-1 sm:truncate">
                      {link}
                    </code>
                    <Button
                      variant="outline"
                      onClick={() => copy(link, 'link')}
                      className="w-full shrink-0 justify-center gap-1.5 sm:w-auto"
                    >
                      {copied === 'link' ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                      {t('teams.join_copy_link')}
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t('teams.join_link_valid_until', { date: expiry })}{' '}
                    <button
                      type="button"
                      onClick={handleRegenerate}
                      disabled={generate.isPending}
                      className="inline-flex items-center gap-1 font-medium text-foreground underline decoration-foreground/30 underline-offset-2 hover:decoration-foreground disabled:opacity-50"
                    >
                      <RefreshCw className="h-3 w-3" />
                      {t('teams.join_generate_new')}
                    </button>
                  </p>
                </div>
              </div>
            </section>

            {/* Method 2 — share code */}
            <section className="rounded-lg border border-border bg-muted/40 p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-card text-[13px] font-semibold tabular-nums text-foreground/80">
                  2
                </span>
                <div className="flex min-w-0 flex-1 flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold">
                      {t('teams.join_share_code')}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('teams.join_share_code_desc', {
                        url: `${window.location.origin}/join`,
                      })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copy(joinToken.code, 'code')}
                    className="w-full shrink-0 rounded-lg border border-border bg-card px-4 py-3 text-center font-mono text-lg font-semibold tracking-wider text-foreground transition-colors hover:bg-muted sm:w-auto sm:py-2"
                    title={t('teams.join_code_copied')}
                  >
                    {joinToken.code}
                  </button>
                </div>
              </div>
            </section>

            {/* Separated from the two method cards — a destructive action
                floating flush under them read as part of step 2. */}
            <div className="flex justify-end border-t border-border pt-3">
              <Button
                variant="ghost"
                onClick={handleRevoke}
                disabled={revoke.isPending}
                className="text-destructive hover:text-destructive"
              >
                {t('teams.join_revoke')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default InviteStudentsModal;
